import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import { ATAJOS_VISOR, BOTONES_VISOR } from "./visorArchivo";

/**
 * Ayuda de la pestaña Bitácora interna del caso. Lo que antes iba en los
 * tooltips del compositor (mensaje rápido, recordatorio, llamada, fotos), de
 * las fichas de cada nota y de Editar/Eliminar.
 *
 * Vive aquí y no en `TabNotas` porque ese archivo ya va en ~1,450 líneas.
 * Mismas condiciones que la pestaña: el compositor sale a admin y soporte con
 * el caso abierto; Editar, al autor o a un admin; Eliminar nota, solo admin.
 */
export function ayudaBitacora({
  puedeEscribir,
  puedeEditar,
  esAdmin,
  cerrado,
  conImeis,
  conMensajesRapidos,
}: {
  /** Admin o soporte con el caso abierto (se ve el compositor). */
  puedeEscribir: boolean;
  /** `canEdit(me)`: admin o soporte. */
  puedeEditar: boolean;
  esAdmin: boolean;
  cerrado: boolean;
  conImeis: boolean;
  /** Hay mensajes rápidos dados de alta (sale su selector). */
  conMensajesRapidos: boolean;
}): ContenidoAyuda {
  const si = (cond: boolean, items: ItemAyuda[]) => (cond ? items : []);
  return {
    manual: "detalle",
    intro: (
      <>
        <p>
          El seguimiento interno del caso: las notas del equipo, las llamadas y lo que el sistema
          registra solo (cambios, correos, respuestas del proveedor). SOLO lo ve el equipo; NO se
          le manda al cliente. Para escribirle al cliente usa la pestaña Correos.
        </p>
        {cerrado && <p>Con el caso cerrado, la bitácora queda como foto del seguimiento.</p>}
      </>
    ),
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          ...si(puedeEscribir, [
            {
              label: "Recuadro de la nota",
              text: "Escribe lo que vayas haciendo. Con @ le avisas a un compañero: le queda pendiente hasta que dé seguimiento en el caso. Con / cambias el estado del equipo o del caso.",
            },
            {
              label: "General del caso / IMEI …",
              text: "Liga la nota a un equipo, o la deja como general del caso.",
            },
          ]),
          ...si(puedeEscribir && conMensajesRapidos, [
            {
              label: "💬 Mensaje rápido…",
              text: "Agrega a la nota una frase ya guardada; sale en un recuadro verde. Las frases las da de alta un admin en Configuración → Correo y empresa → Mensajes rápidos.",
            },
          ]),
          ...si(puedeEscribir, [
            {
              label: "Agregar a bitácora",
              text: "Guarda la nota (también con Ctrl+Enter). Sale gris si no hay texto, mensaje rápido ni foto.",
            },
            {
              label: "⏰ Recordatorio",
              text: "Crea un recordatorio junto con la nota: eliges cuándo (en 1, 2 o 4 horas, mañana 9:00 o una fecha exacta) y usa el texto de la nota. Avisa a quien abrió el caso y al asignado. Lo cambias después en la pestaña Recordatorios.",
            },
            {
              label: "Registrar llamada",
              text: "Anota una llamada, videollamada u otro contacto con el cliente: cuándo, cuánto duró, con quién y qué se habló. Queda en la bitácora.",
            },
            {
              label: "📎 Fotos",
              text: "Sube fotos de constancia; también puedes arrastrarlas a cualquier parte del recuadro. Solo imágenes. Se guardan en Evidencias al momento, aunque no llegues a guardar la nota, y no se preseleccionan al armar el ZIP del proveedor.",
            },
            {
              label: "🖼️ De Evidencias",
              text: "Cita en la nota una foto que ya está en Evidencias, sin volver a subirla. Puedes elegir varias.",
            },
            {
              label: "× (en una foto de la nota)",
              text: "La quita de la nota que estás escribiendo; la foto se queda en Evidencias.",
            },
          ]),
          ...si(puedeEscribir && conImeis, [
            {
              label: "± Cambiar estado del equipo",
              text: "Junto con la nota, fija el estado del equipo elegido arriba; si la nota es «General del caso», el de todos (los CRIT se respetan). Tu nota queda como justificación.",
            },
          ]),
          ...si(puedeEscribir, [
            {
              label: "± Cambiar estado del caso",
              text: "Junto con la nota, fija el estado del caso sin tocar el de los equipos.",
            },
            {
              label: "Deshacer",
              text: "Sale después de cambiar el estado de los equipos: los regresa como estaban, y el caso también. La nota se queda.",
            },
          ]),
          {
            label: "Todo / Conversación / Eventos del sistema",
            text: "Conversación deja las notas, las llamadas, el ida y vuelta con el proveedor y los correos; Eventos del sistema, los cambios del expediente (estados, fechas, equipos, apertura y cierre).",
          },
          ...si(conImeis, [
            {
              label: "Todos los equipos / General del caso / IMEI …",
              text: "Deja solo las notas de ese equipo, o solo las generales.",
            },
          ]),
          {
            label: "✉️ ver en correos →",
            text: "Te lleva a la pestaña Correos con ese correo abierto.",
          },
          { label: "Una foto en una nota", text: "Tócala para verla en grande." },
          ...si(puedeEditar && !cerrado, [
            {
              label: "Editar",
              text: "Corrige el texto de una nota tuya (un admin, de cualquiera). Las notas de apertura y cierre del caso no se editan.",
            },
          ]),
          ...si(esAdmin && !cerrado, [
            {
              label: "Eliminar (una nota)",
              text: "Solo admin. Borra la nota para siempre, después de confirmar. También aplica a las que escribió el sistema; solo las de apertura y cierre no se borran.",
            },
          ]),
          ...si(puedeEditar && !cerrado, [
            {
              label: "Eliminar (una llamada)",
              text: "Borra el registro de esa llamada, después de confirmar. No se puede recuperar.",
            },
          ]),
          ...BOTONES_VISOR,
        ],
      },
      {
        titulo: "Qué significa cada indicador",
        items: [
          { label: "IMEI …", text: "La nota es de ese equipo." },
          {
            label: "⏰ fecha · en 2 h",
            text: "La nota creó un recordatorio para esa fecha (está en la pestaña Recordatorios). Tachado y en gris: ya se completó o se canceló.",
          },
          { label: "#etiqueta", text: "Las #palabras de la nota, o las que pone el sistema." },
          { label: "💬 recuadro verde", text: "Un mensaje rápido que se agregó a la nota." },
          {
            label: "VIDEOLLAMADA, LLAMADA…",
            text: "Una llamada registrada, con su duración y con quién fue.",
          },
          {
            label: "SISTEMA / AUTO (bajo el autor)",
            text: "La entrada no la escribió una persona: la registró el sistema solo.",
          },
          {
            label: "archivo eliminado",
            text: "La foto se borró del servidor por retención (18 meses); la nota se queda.",
          },
          ...si(puedeEscribir, [
            {
              label: "Avisar a: @…",
              text: "A quién le llegará el aviso de tu nota; la ✕ lo quita.",
            },
          ]),
        ],
      },
      {
        titulo: "Atajos de teclado",
        items: [
          ...si(puedeEscribir, [
            { label: "Ctrl + Enter", text: "Guarda la nota (también al editar una)." },
            {
              label: "↑ / ↓, Enter, Esc",
              text: "Con el menú de @ o de / abierto: recorren, eligen y lo cierran.",
            },
          ]),
          ...si(puedeEditar && !cerrado, [{ label: "Esc", text: "Al editar una nota, cancela." }]),
          ...ATAJOS_VISOR,
        ],
      },
    ],
  };
}
